import type { Models } from '@smartlinkdev/types';
import i18n, { type Config } from 'sveltekit-i18n';
import { get } from 'svelte/store';
import { browser } from '$app/environment';
import { activeTranslations } from '$lib/stores/languages';
import lang from './lang.json';

export enum Locales {
  DE = 'de',
  EN = 'en',
  ES = 'es',
  FR = 'fr',
  KO = 'ko',
  PT = 'pt',
  RU = 'ru',
  TL = 'tl',
  VI = 'vi',
  ZH = 'zh',
}

const config: Config = ({
  translations: {
    [Locales.DE]: { lang },
    [Locales.EN]: { lang },
    [Locales.ES]: { lang },
    [Locales.FR]: { lang },
    [Locales.KO]: { lang },
    [Locales.PT]: { lang },
    [Locales.RU]: { lang },
    [Locales.TL]: { lang },
    [Locales.VI]: { lang },
    [Locales.ZH]: { lang },
  },
  loaders: [
    {
      locale: Locales.DE,
      key: 'content',
      loader: async () => (await import('./de/content.json')).default,
    },
    {
      locale: Locales.EN,
      key: 'content',
      loader: async () => (await import('./en/content.json')).default,
    },
    {
      locale: Locales.ES,
      key: 'content',
      loader: async () => (await import('./es/content.json')).default,
    },
    {
      locale: Locales.FR,
      key: 'content',
      loader: async () => (await import('./fr/content.json')).default,
    },
    {
      locale: Locales.KO,
      key: 'content',
      loader: async () => (await import('./ko/content.json')).default,
    },
    {
      locale: Locales.PT,
      key: 'content',
      loader: async () => (await import('./pt/content.json')).default,
    },
    {
      locale: Locales.RU,
      key: 'content',
      loader: async () => (await import('./ru/content.json')).default,
    },
    {
      locale: Locales.TL,
      key: 'content',
      loader: async () => (await import('./tl/content.json')).default,
    },
    {
      locale: Locales.VI,
      key: 'content',
      loader: async () => (await import('./vi/content.json')).default,
    },
    {
      locale: Locales.ZH,
      key: 'content',
      loader: async () => (await import('./zh/content.json')).default,
    },
  ],
});

export const { t, loading, locales, locale, loadTranslations, setLocale } =  new i18n(config);

export const supportedLocales = Object.values<string>(Locales);

const normalizeLocale = (locale: string | undefined): string | undefined => {
  if (!locale) {
    return undefined;
  }
  if (locale.length === 2) {
    return locale;
  }
  const normalized = locale.split('-')[0];
  if (normalized.length === 2) {
    return normalized;
  }
  return undefined;
}

const isValidLocale = (locale: string): boolean => {
  const normalizedLocale = normalizeLocale(locale);
  if (!normalizedLocale) {
    return false;
  }
  return supportedLocales.includes(normalizedLocale);
}

const localeCookieName = 'locale';

const getCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
};

export const getInitLocale = (): string => {
  const localeCookie = getCookie(localeCookieName);
  if (localeCookie && isValidLocale(localeCookie)) {
    return localeCookie;
  }

  if (browser) {
    const browserLocale = navigator.language;
    if (browserLocale && isValidLocale(browserLocale)) {
      return normalizeLocale(browserLocale) as string;
    }
  }

  // Default to en
  return Locales.EN;
};

interface GetTranslationConfig {
  modelTable: string;
  modelAttribute: string;
  modelId: number;
}

export function getTranslation(config: GetTranslationConfig): Models.Translation | undefined {
  const active = get(activeTranslations);
  return active.find((translation: Models.Translation) => {
    return translation.model_table === config.modelTable
      && translation.model_attribute === config.modelAttribute
      && translation.model_id === config.modelId;
  });
}
